import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { FileText, Plus, MoreHorizontal } from 'lucide-react';
import { PipeSelector } from '../../../../../components/ui/PipeSelector';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../../../../components/ui/custom-dropdown-menu';
import { Label } from '../../../../../components/ui/label';
import type { DashboardProject } from '../../../types/project';
import { AddEquipmentModal } from './AddEquipmentModal';
import { EquipmentTable } from './EquipmentTable';
import { AddNewRegistryItemModal } from './AddNewRegistryItemModal';
import { getListMaterialCategory } from '../../../../../redux/Project/materialSaga';
import { NDEConsumableTable } from './NDEConsumableTable';
import {
  MiscellaneousRegistryTable,
  type MiscellaneousRegistryTableHandle,
} from './MiscellaneousRegistryTable';
import { QCInspectionPersonnelTable } from './QCInspectionPersonnelTable';
import {
  WelderQualificationRegisterTable,
  type WelderQualificationRegisterTableHandle,
} from './WelderQualificationRegisterTable';
//import { GasTab } from './tabs/GasConsumable';
//import { WireTab } from './tabs/WireConsumable';
import { AddNDEConsumableModal } from './AddNDEConsumableModal';
import { AddQCInspectionPersonnelModal } from './AddQCInspectionPersonnelModal';
//import { getAllConsumablesSaga } from '../../../../../redux/Project/registrySaga';
import { MaterialData } from '../../../types/materials';
import { EquipmentSignatureReportModal } from './EquipmentSignatureReportModal';
import { EquipmentReportsViewModal } from './EquipmentReportsViewModal';
import { ARKRegistryDocumentTable } from './ARKRegistryDocumentTable';
import { useUserPermissionsAfterLogin } from '../../../../admin/hooks/useUserPermissionsAfterLogin';

interface EquipmentAndConsumableRegistryProps {
  project: DashboardProject;
  canEdit?: boolean;
  canReport?: boolean;
}

interface RegistryItem {
  tags_id: number;
  tags_name: string;
  tags_type: string;
}

type RegistryOption = {
  value: string;
  label: string;
  registryType: string;
  subTab: string;
  /** Spool fabrication projects only */
  spoolOnly?: boolean;
};

export type PersonnelCategory = 'qc' | 'hydro' | 'torquing';

const HYDRO_EQUIPMENT_ITEM: RegistryItem = {
  tags_id: 5,
  tags_name: 'Hydro Testing/Flushing of pipe',
  tags_type: 'REGISTRY_ITEM',
};
const TORQUING_EQUIPMENT_ITEM: RegistryItem = {
  tags_id: 6,
  tags_name: 'Torquing',
  tags_type: 'REGISTRY_ITEM',
};
const GAUGING_EQUIPMENT_ITEM: RegistryItem = {
  tags_id: 7,
  tags_name: 'Gauging of pipe',
  tags_type: 'REGISTRY_ITEM',
};

const HYDRO_CONSUMABLE_ITEM: RegistryItem = {
  tags_id: 2,
  tags_name: 'Hydro Testing/Flushing of pipe',
  tags_type: 'REGISTRY_ITEM',
};
const TORQUING_CONSUMABLE_ITEM: RegistryItem = {
  tags_id: 3,
  tags_name: 'Torquing',
  tags_type: 'REGISTRY_ITEM',
};

const CONSUMABLE_CATEGORY_BY_SUB_TAB: Record<string, string> = {
  nde: 'NDE',
  coating: 'Coating',
  'hydro-testing-flushing-of-pipe': 'Hydro Testing/Flushing of pipe',
  torquing: 'Torquing',
};

const REGISTRY_OPTIONS: RegistryOption[] = [
  { value: 'equipment-welding', label: 'Equipment -  Welding', registryType: 'Equipment', subTab: 'welding' },
  { value: 'equipment-nde', label: 'Equipment - NDE', registryType: 'Equipment', subTab: 'nde' },
  { value: 'equipment-qc', label: 'Equipment - QC', registryType: 'Equipment', subTab: 'qc' },
  { value: 'equipment-coating', label: 'Equipment - Coating', registryType: 'Equipment', subTab: 'coating' },
  { value: 'equipment-hydro-testing-flushing-of-pipe', label: 'Equipment - Hydro Testing/Flushing of pipe', registryType: 'Equipment', subTab: 'hydro-testing-flushing-of-pipe' },
  { value: 'equipment-torquing', label: 'Equipment - Torquing', registryType: 'Equipment', subTab: 'torquing' },
  { value: 'equipment-gauging-of-pipe', label: 'Equipment - Gauging of pipe', registryType: 'Equipment', subTab: 'gauging-of-pipe', spoolOnly: true },
  { value: 'consumable-nde', label: 'Consumable - NDE', registryType: 'Consumable', subTab: 'nde' },
  { value: 'consumable-coating', label: 'Consumable - Coating', registryType: 'Consumable', subTab: 'coating' },
  { value: 'consumable-hydro-testing-flushing-of-pipe', label: 'Consumable - Hydro Testing/Flushing of pipe', registryType: 'Consumable', subTab: 'hydro-testing-flushing-of-pipe' },
  { value: 'consumable-torquing', label: 'Consumable - Torquing', registryType: 'Consumable', subTab: 'torquing' },
  { value: 'qc-personnel', label: 'QC Inspection Personnel', registryType: 'QC Inspection Personnel', subTab: '' },
  { value: 'hydro-personnel', label: 'Hydro Testing Personnel', registryType: 'Hydro Testing Personnel', subTab: '' },
  { value: 'torquing-personnel', label: 'Torquing Personnel', registryType: 'Torquing Personnel', subTab: '' },
  { value: 'welder-qualification', label: 'Welder Certification & Qualification', registryType: 'Welder Certification & Qualification', subTab: '' },
  { value: 'miscellaneous', label: 'Miscellaneous', registryType: 'Miscellaneous', subTab: '' },
];

export const EquipmentAndConsumableRegistry: React.FC<EquipmentAndConsumableRegistryProps> = ({ project, canEdit = true, canReport = true }) => {
  const [registryTypes, setRegistryTypes] = useState<any[]>([]);
  const [registryItems, setRegistryItems] = useState<RegistryItem[]>([]);
  const [activeSubTab, setActiveSubTab] = useState<string>('');
  const [isAddEquipmentModalOpen, setIsAddEquipmentModalOpen] = useState(false);
  const [isAddRegistryItemModalOpen, setIsAddRegistryItemModalOpen] = useState(false);
  const [equipmentType, setEquipmentType] = useState<string>('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRegistryType, setSelectedRegistryType] = useState<string>('Equipment');
  const [materialsData, setMaterialsData] = useState<any>(null);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [isSignatureReportModalOpen, setIsSignatureReportModalOpen] = useState(false);
  const [isReportsViewModalOpen, setIsReportsViewModalOpen] = useState(false);
  const [isAddConsumableModalOpen, setIsAddConsumableModalOpen] = useState(false);
  const [isAddPersonnelModalOpen, setIsAddPersonnelModalOpen] = useState(false);
  const welderTableRef = useRef<WelderQualificationRegisterTableHandle>(null);
  const miscellaneousRegistryTableRef = useRef<MiscellaneousRegistryTableHandle>(null);

  // Get user permissions
  const { userRole, userPermissions, isAdminOrHigher } = useUserPermissionsAfterLogin();

  const isSpoolProject = useMemo(() => {
    return Boolean(project.project?.projectTypes?.fabrication?.options?.spools?.selected);
  }, [project]);

  const registryOptions = useMemo(() => {
    return REGISTRY_OPTIONS.filter((option) => !option.spoolOnly || isSpoolProject);
  }, [isSpoolProject]);

  const personnelCategory: PersonnelCategory = useMemo(() => {
    if (selectedRegistryType === 'Hydro Testing Personnel') return 'hydro';
    if (selectedRegistryType === 'Torquing Personnel') return 'torquing';
    return 'qc';
  }, [selectedRegistryType]);

  const isPersonnelRegistry =
    selectedRegistryType === 'QC Inspection Personnel' ||
    selectedRegistryType === 'Hydro Testing Personnel' ||
    selectedRegistryType === 'Torquing Personnel';

  // Calculate user role flags
  const isCustomerRepresentativeUser = useMemo(() => {
    return (project.project as any)?.team_member_role === 'Customer Representative';
  }, [project]);

  const isQCInspectorUser = useMemo(() => {
    return (project.project as any)?.team_member_role === 'QC Inspector' ||
      (project.project as any)?.team_member_role === 'Quality Control' ||
      (project.project as any)?.team_member_role === 'QA/QC';
  }, [project]);

  const isWelderUser = useMemo(() => {
    return (project.project as any)?.team_member_role === 'Welder' ||
      (project.project as any)?.team_member_role === 'Welding';
  }, [project]);

  const currentUserTeamMemberId = useMemo(() => {
    return userPermissions?.user_id || null;
  }, [userPermissions]);

  const isAdminOrSuperAdmin = useMemo(() => {
    return isAdminOrHigher();
  }, [isAdminOrHigher]);

  // Fetch registry items on mount
  useEffect(() => {
    const loadRegistryItems = async () => {
      try {
        setIsLoading(true);
        const registryTypesData: RegistryItem[] = [
          { tags_id: 1, tags_name: 'Equipment', tags_type: 'REGISTRY_ITEM_TYPE' },
          { tags_id: 2, tags_name: 'Consumable', tags_type: 'REGISTRY_ITEM_TYPE' },
          // { tags_id: 3, tags_name: 'ARK Registry', tags_type: 'REGISTRY_ITEM_TYPE' },
           { tags_id: 4, tags_name: 'QC Inspection Personnel', tags_type: 'REGISTRY_ITEM_TYPE' },
          { tags_id: 5, tags_name: 'Welder Certification & Qualification', tags_type: 'REGISTRY_ITEM_TYPE' },
          { tags_id: 6, tags_name: 'Miscellaneous', tags_type: 'REGISTRY_MISC_TYPE' },
        ];
        setRegistryTypes(registryTypesData);

        const defaultItems: RegistryItem[] = [
          { tags_id: 1, tags_name: 'Welding', tags_type: 'REGISTRY_ITEM' },
          { tags_id: 2, tags_name: 'NDE', tags_type: 'REGISTRY_ITEM' },
          { tags_id: 3, tags_name: 'QC', tags_type: 'REGISTRY_ITEM' },
          { tags_id: 4, tags_name: 'Coating', tags_type: 'REGISTRY_ITEM' },
          HYDRO_EQUIPMENT_ITEM,
          TORQUING_EQUIPMENT_ITEM,
          ...(isSpoolProject ? [GAUGING_EQUIPMENT_ITEM] : []),
        ];
        setRegistryItems(defaultItems);
        if (!activeSubTab) {
          setActiveSubTab('welding');
        }
        // Fetch registry items with specific type as requested
        // const registryItemTags = await getListMaterialCategory('REGISTRY_ITEM');

        // if (registryItemTags && registryItemTags.length > 0) {
        //   // const defaultItems: RegistryItem[] = [
        //   //   { tags_id: 1, tags_name: 'Welding', tags_type: 'REGISTRY_ITEM' },
        //   //   { tags_id: 2, tags_name: 'NDE', tags_type: 'REGISTRY_ITEM' },
        //   //   { tags_id: 3, tags_name: 'QC', tags_type: 'REGISTRY_ITEM' },
        //   // ];
        //   // Remove duplicates and sort companies alphabetically by company_name
        //   const uniqueRegistryItemTags = registryItemTags
        //     .filter((tag: any, index: number, self: any[]) =>
        //       index === self.findIndex((t: any) => t.tags_name === tag.tags_name)
        //     )
        //     .sort((a: any, b: any) => a.tags_name.localeCompare(b.tags_name));

        //   setRegistryItems(uniqueRegistryItemTags);
        //   if (!activeSubTab) {
        //     setActiveSubTab(getTabValue(registryItemTags[0].tags_name));
        //   }
        // } else {
        // Fallback to default items if no registry items found
        // const defaultItems: RegistryItem[] = [
        //   { tags_id: 1, tags_name: 'Welding', tags_type: 'REGISTRY_ITEM' },
        //   { tags_id: 2, tags_name: 'NDE', tags_type: 'REGISTRY_ITEM' },
        //   { tags_id: 3, tags_name: 'QC', tags_type: 'REGISTRY_ITEM' },
        // ];
        // setRegistryItems(defaultItems);
        // if (!activeSubTab) {
        //   setActiveSubTab('welding');
        // }
        // }
      } catch (error) {
        console.error('Failed to load registry items:', error);
        // Fallback to default items on error
        // const defaultItems: RegistryItem[] = [
        //   { tags_id: 1, tags_name: 'Welding', tags_type: 'REGISTRY_ITEM' },
        //   { tags_id: 2, tags_name: 'NDE', tags_type: 'REGISTRY_ITEM' },
        //   { tags_id: 3, tags_name: 'QC', tags_type: 'REGISTRY_ITEM' },

        // ];
        // setRegistryItems(defaultItems);
        // if (!activeSubTab) {
        //   setActiveSubTab('welding');
        // }
      } finally {
        setIsLoading(false);
      }
    };

    loadRegistryItems();
  }, [isSpoolProject]);
  const getconsumableItems = (): RegistryItem[] => {
    return [
      { tags_id: 1, tags_name: 'NDE', tags_type: 'REGISTRY_ITEM' },
      { tags_id: 2, tags_name: 'Coating', tags_type: 'REGISTRY_ITEM' },
      HYDRO_CONSUMABLE_ITEM,
      TORQUING_CONSUMABLE_ITEM,
    ];
  };

  // Get current items based on selected registry type
  const currentItems = useMemo(() => {
    if (selectedRegistryType === 'Consumable') {
      return getconsumableItems();
    }
    return registryItems;
  }, [selectedRegistryType, registryItems, isSpoolProject]);

  useEffect(() => {
    const currentValue =
      selectedRegistryType === 'QC Inspection Personnel'
        ? 'qc-personnel'
        : selectedRegistryType === 'Hydro Testing Personnel'
        ? 'hydro-personnel'
        : selectedRegistryType === 'Torquing Personnel'
        ? 'torquing-personnel'
        : selectedRegistryType === 'Welder Certification & Qualification'
        ? 'welder-qualification'
        : selectedRegistryType === 'Miscellaneous'
        ? 'miscellaneous'
        : selectedRegistryType === 'Equipment'
        ? `equipment-${activeSubTab}`
        : selectedRegistryType === 'Consumable'
        ? `consumable-${activeSubTab}`
        : null;

    if (currentValue && !registryOptions.some((option) => option.value === currentValue)) {
      const fallbackOption = registryOptions[0];
      if (fallbackOption) {
        setSelectedRegistryType(fallbackOption.registryType);
        setActiveSubTab(fallbackOption.subTab);
      }
    }
  }, [activeSubTab, registryOptions, selectedRegistryType]);

  const activeConsumableCategory = CONSUMABLE_CATEGORY_BY_SUB_TAB[activeSubTab] || 'NDE';

  // Convert registry item name to a valid tab value (e.g. "Hydro Testing/Flushing of pipe" → hydro-testing-flushing-of-pipe)
  const getTabValue = (itemName: string): string => {
    return itemName
      .toLowerCase()
      .replace(/[\/\\]+/g, '-')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  // Get display name from tab value
  const getDisplayName = (tabValue: string): string => {
    const item = registryItems.find(item => getTabValue(item.tags_name) === tabValue);
    return item ? item.tags_name : tabValue;
  };

  const handleAddEquipment = () => {
    setEquipmentType(activeSubTab);
    setIsAddEquipmentModalOpen(true);
  };

  const handleEquipmentAdded = (type: string) => {
    setActiveSubTab(type);
    setIsAddEquipmentModalOpen(false);
    // Trigger refresh by updating refreshKey
    setRefreshKey(prev => prev + 1);
  };

  const handleRefresh = useCallback(() => {
    // Trigger refresh by updating refreshKey
    // setRefreshKey(prev => prev + 1);
    // // Also refresh materials data
    // if (materialsData === null && selectedRegistryType === 'Consumable' && project?.project?.projectId) {
    //   fetchMaterials();
    // }
  }, [selectedRegistryType, project?.project?.projectId]);

  // // Fetch materials data
  // const fetchMaterials = useCallback(async () => {
  //   if (!project?.project?.projectId) return;

  //   try {
  //     setMaterialsLoading(true);
  //     const data = await getAllConsumablesSaga({ project_id: project.project.projectId });
  //     setMaterialsData(data);
  //   } catch (error) {
  //     console.error('Failed to fetch materials:', error);
  //     setMaterialsData(null);
  //   } finally {
  //     setMaterialsLoading(false);
  //   }
  // }, [project?.project?.projectId]);

  // // Fetch materials when Consumable tab is selected
  // useEffect(() => {
  //   if (materialsData === null && selectedRegistryType === 'Consumable' && project?.project?.projectId) {
  //     fetchMaterials();
  //   }
  // }, [selectedRegistryType, project?.project?.projectId, fetchMaterials]);

  // Helper function to get materials by type
  // const getmaterials = useCallback((type: string): MaterialData[] => {
  //   if (!materialsData) return [];

  //   if (type === 'gas') {
  //     return materialsData.gas || [];
  //   } else if (type === 'wire') {
  //     return materialsData.wireLot || [];
  //   }
  //   return [];
  // }, [materialsData]);

  const handleAddRegistryItem = async (itemName: string) => {
    // Reload registry items to get the new one
    try {
      const registryItemTags = await getListMaterialCategory('REGISTRY_ITEM');
      const uniqueRegistryItemTags = registryItemTags
        .filter((tag: any, index: number, self: any[]) =>
          index === self.findIndex((t: any) => t.tags_name === tag.tags_name)
        )
        .sort((a: any, b: any) => a.tags_name.localeCompare(b.tags_name));

      if (uniqueRegistryItemTags && uniqueRegistryItemTags.length > 0) {
        setRegistryItems(uniqueRegistryItemTags);
        // Switch to the newly added tab
        const newTabValue = getTabValue(itemName);
        setActiveSubTab(newTabValue);
      }
    } catch (error) {
      console.error('Failed to reload registry items:', error);
    }
  };

  // Handle generate report button click - works for Equipment, Consumable, and personnel registries
  const handleGenerateReport = useCallback(() => {
    if (
      selectedRegistryType !== 'Equipment' &&
      selectedRegistryType !== 'Consumable' &&
      selectedRegistryType !== 'QC Inspection Personnel' &&
      selectedRegistryType !== 'Hydro Testing Personnel' &&
      selectedRegistryType !== 'Torquing Personnel'
    ) {
      return;
    }
    setIsSignatureReportModalOpen(true);
  }, [selectedRegistryType]);

  // Handle reports view button click - opens the reports view modal
  const handleReportsView = useCallback(() => {
    setIsReportsViewModalOpen(true);
  }, []);


  return (
    <div className="space-y-6 min-w-0">
      <Card className="border-none shadow-none min-w-0">
        <CardContent className="p-4 sm:p-6">
          <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
              <Label className="shrink-0 whitespace-nowrap text-[12px] font-medium text-text-grey">Registry:</Label>
              <PipeSelector
                triggerWidth="w-full sm:w-[400px]"
                value={
                  selectedRegistryType === 'QC Inspection Personnel'
                    ? 'qc-personnel'
                    : selectedRegistryType === 'Hydro Testing Personnel'
                    ? 'hydro-personnel'
                    : selectedRegistryType === 'Torquing Personnel'
                    ? 'torquing-personnel'
                    : selectedRegistryType === 'Welder Certification & Qualification'
                    ? 'welder-qualification'
                    : selectedRegistryType === 'Miscellaneous'
                    ? 'miscellaneous'
                    : selectedRegistryType === 'Equipment'
                    ? `equipment-${activeSubTab}`
                    : selectedRegistryType === 'Consumable'
                    ? `consumable-${activeSubTab}`
                    : null
                }
                onChange={(value: string) => {
                  const option = registryOptions.find((o) => o.value === value);
                  if (!option) return;
                  setSelectedRegistryType(option.registryType);
                  setActiveSubTab(option.subTab);
                }}
                placeholder="Select registry"
                options={registryOptions.map((o) => ({ value: o.value, label: o.label }))}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {selectedRegistryType === 'Equipment' && canEdit && (
                <Button
                  onClick={handleAddEquipment}
                  size="sm"
                  className="bg-hero-red text-white hover:bg-hero-red-60 flex items-center gap-2 justify-center"
                >
                  <Plus className="h-4 w-4" />
                  Add New Equipment
                </Button>
              )}
              {selectedRegistryType === 'Consumable' && canEdit && (
                <Button
                  onClick={() => setIsAddConsumableModalOpen(true)}
                  size="sm"
                  className="bg-hero-red text-white hover:bg-hero-red-60 flex items-center gap-2 justify-center"
                >
                  <Plus className="h-4 w-4" />
                  Add New Consumable
                </Button>
              )}
              {isPersonnelRegistry && canEdit && (
                <Button
                  onClick={() => setIsAddPersonnelModalOpen(true)}
                  size="sm"
                  className="bg-hero-red text-white hover:bg-hero-red-60 flex items-center gap-2 justify-center"
                >
                  <Plus className="h-4 w-4" />
                  Add New Personnel
                </Button>
              )}
              {selectedRegistryType === 'Welder Certification & Qualification' && canEdit && (
                <Button
                  type="button"
                  size="sm"
                  className="bg-hero-red text-white hover:bg-hero-red-60 flex items-center gap-2 justify-center"
                  onClick={() => welderTableRef.current?.openAddWelderRow()}
                >
                  <Plus className="h-4 w-4" />
                  Add welder row
                </Button>
              )}
              {selectedRegistryType === 'Miscellaneous' && canEdit && (
                <Button
                  type="button"
                  size="sm"
                  className="bg-hero-red text-white hover:bg-hero-red-60 flex items-center gap-2 justify-center"
                  onClick={() => miscellaneousRegistryTableRef.current?.openAdd()}
                >
                  <Plus className="h-4 w-4" />
                  Add Miscellaneous Row
                </Button>
              )}
              {canReport && (
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center justify-center w-8 h-8 rounded-lg border border-[#E2E5EA] bg-white hover:bg-page-bg transition-colors">
                    <MoreHorizontal className="h-4 w-4 text-text-grey" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleReportsView()}>
                      <FileText className="h-4 w-4 mr-2" />
                      Reports
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setIsSignatureReportModalOpen(true)}>
                      <FileText className="h-4 w-4 mr-2" />
                      Generate Report
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {selectedRegistryType === 'Equipment' && (
            <EquipmentTable
              key={`${activeSubTab}-${refreshKey}`}
              equipmentType={activeSubTab}
              project={project}
              onRefresh={handleRefresh}
            />
          )}
          {selectedRegistryType === 'Consumable' && activeConsumableCategory && (
            <NDEConsumableTable
              project={project}
              onRefresh={handleRefresh}
              consumableType={activeConsumableCategory}
            />
          )}
          {isPersonnelRegistry && (
            <QCInspectionPersonnelTable
              key={personnelCategory}
              project={project}
              onRefresh={handleRefresh}
              personnelCategory={personnelCategory}
            />
          )}
          {selectedRegistryType === 'Welder Certification & Qualification' && (
            <WelderQualificationRegisterTable
              ref={welderTableRef}
              project={project}
              canEdit={canEdit}
              showTableToolbarAddButton={false}
            />
          )}
          {selectedRegistryType === 'Miscellaneous' && (
            <MiscellaneousRegistryTable
              ref={miscellaneousRegistryTableRef}
              project={project}
              onRefresh={handleRefresh}
              canEdit={canEdit}
            />
          )}
          {selectedRegistryType === 'ARK Registry' && (
            <div className="h-[calc(100vh-300px)] min-h-[400px]">
              <ARKRegistryDocumentTable
                projectId={project?.project?.projectId ? Number(project.project.projectId) : 0}
                projectName={project?.project?.projectName}
                canEdit={isAdminOrSuperAdmin || !isWelderUser}
              />
            </div>
          )}
        </CardContent>
      </Card>

    {isAddEquipmentModalOpen && <AddEquipmentModal
        isOpen={isAddEquipmentModalOpen}
        onClose={() => setIsAddEquipmentModalOpen(false)}
        onAdd={handleEquipmentAdded}
        equipmentType={equipmentType}
        project={project}
      />}

      {isAddRegistryItemModalOpen && <AddNewRegistryItemModal
        isOpen={isAddRegistryItemModalOpen}
        onClose={() => setIsAddRegistryItemModalOpen(false)}
        onAdd={handleAddRegistryItem}
        project={project}
        existingItems={registryItems.map(item => item.tags_name)}
      />}

      {isSignatureReportModalOpen && <EquipmentSignatureReportModal
        isOpen={isSignatureReportModalOpen}
        onClose={() => {
          setIsSignatureReportModalOpen(false);
        }}
        registryItems={
          selectedRegistryType === 'Welder Certification & Qualification' ||
          selectedRegistryType === 'Miscellaneous' ||
          isPersonnelRegistry
            ? []
            : currentItems
        }
        registryType={
          selectedRegistryType as
            | 'Equipment'
            | 'Consumable'
            | 'QC Inspection Personnel'
            | 'Hydro Testing Personnel'
            | 'Torquing Personnel'
            | 'Welder Certification & Qualification'
            | 'Miscellaneous'
        }
        activeTabName={activeSubTab}
        personnelCategory={isPersonnelRegistry ? personnelCategory : undefined}
        projectId={project?.project?.projectId ? Number(project.project.projectId) : undefined}
        projectName={project?.project?.projectName}
        isCustomerRepresentativeUser={isCustomerRepresentativeUser}
        isQCInspectorUser={isQCInspectorUser}
        isWelderUser={isWelderUser}
        currentUserTeamMemberId={currentUserTeamMemberId}
        canSign={canReport}
        canEdit={canEdit}
        isAdminOrSuperAdmin={isAdminOrSuperAdmin}
        onRefresh={handleRefresh}
      />}

      {isReportsViewModalOpen && <EquipmentReportsViewModal
        isOpen={isReportsViewModalOpen}
        onClose={() => setIsReportsViewModalOpen(false)}
        onGenerateClick={() => {
          setIsReportsViewModalOpen(false);
          setIsSignatureReportModalOpen(true);
        }}
        projectId={project?.project?.projectId ? Number(project.project.projectId) : 0}
        projectName={project?.project?.projectName || ''}
        activeTabName={activeSubTab}
        registryType={
          selectedRegistryType as
            | 'Equipment'
            | 'Consumable'
            | 'QC Inspection Personnel'
            | 'Hydro Testing Personnel'
            | 'Torquing Personnel'
            | 'Welder Certification & Qualification'
            | 'Miscellaneous'
        }
      />}

      {isAddConsumableModalOpen && <AddNDEConsumableModal
        isOpen={isAddConsumableModalOpen}
        onClose={() => setIsAddConsumableModalOpen(false)}
        onAdd={() => {
          setIsAddConsumableModalOpen(false);
          handleRefresh();
        }}
        project={project}
        consumableCategory={`${activeConsumableCategory} Consumables`}
      />}

      {isAddPersonnelModalOpen && <AddQCInspectionPersonnelModal
        isOpen={isAddPersonnelModalOpen}
        onClose={() => setIsAddPersonnelModalOpen(false)}
        onAdd={() => {
          setIsAddPersonnelModalOpen(false);
          handleRefresh();
        }}
        project={project}
        personnelCategory={personnelCategory}
      />}

       
    </div>
  );
};

